import { useState, useCallback } from 'react';
import {
  Field,
  Text,
  Spinner,
  Card,
  CardHeader,
  makeStyles,
  tokens,
  Persona,
  Combobox,
  Option,
  useId,
} from '@fluentui/react-components';
import { searchUsers, getUserDetails } from '../../../api/client';
import { GraphUser, HardwareRequestFormData } from '../../../hooks/useHardwareRequestForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  searchField: {
    maxWidth: '500px',
  },
  userCard: {
    maxWidth: '600px',
    marginTop: tokens.spacingVerticalM,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  infoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '120px',
  },
});

interface UserSelectionStepProps {
  formData: HardwareRequestFormData;
  onUserSelect: (user: GraphUser) => void;
}

export function UserSelectionStep({ formData, onUserSelect }: UserSelectionStepProps) {
  const styles = useStyles();
  const comboId = useId('user-search');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounced search
  const handleSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const results = await searchUsers(query);
        setSearchResults(results);
      } catch (err) {
        setError('Failed to search users');
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  // Handle user selection
  const handleUserSelect = async (userId: string) => {
    setIsLoadingDetails(true);
    setError(null);

    try {
      const userDetails = await getUserDetails(userId);
      setSelectedUser(userDetails);
      onUserSelect(userDetails);
    } catch (err) {
      setError('Failed to load user details');
      console.error('Load details error:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <div className={styles.container}>
      <Text size={500} weight="semibold">
        Select Employee
      </Text>
      <Text>Search for the employee who will receive the hardware.</Text>

      <Field label="Search for employee">
        <Combobox
          id={comboId}
          placeholder="Type to search..."
          value={searchQuery}
          onInput={(e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchQuery(value);
            handleSearch(value);
          }}
          onOptionSelect={(_, data) => {
            if (data.optionValue) {
              handleUserSelect(data.optionValue);
            }
          }}
          className={styles.searchField}
        >
          {isSearching && (
            <Option key="searching" disabled text="Searching...">
              <Spinner size="tiny" label="Searching..." />
            </Option>
          )}
          {!isSearching &&
            searchResults.map((user) => (
              <Option key={user.id} value={user.id} text={user.displayName}>
                <Persona
                  name={user.displayName}
                  secondaryText={user.mail || user.userPrincipalName}
                  size="small"
                />
              </Option>
            ))}
          {!isSearching && searchResults.length === 0 && searchQuery.length >= 2 && (
            <Option key="no-results" disabled text="No users found">
              No users found
            </Option>
          )}
        </Combobox>
      </Field>

      {error && (
        <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
      )}

      {isLoadingDetails && <Spinner label="Loading user details..." />}

      {selectedUser && formData.selectedUserId && (
        <Card className={styles.userCard}>
          <CardHeader
            image={
              <Persona
                name={selectedUser.displayName}
                size="large"
              />
            }
            header={<Text weight="semibold">{selectedUser.displayName}</Text>}
            description={<Text>{selectedUser.mail || selectedUser.userPrincipalName}</Text>}
          />
          <div className={styles.cardContent}>
            {selectedUser.jobTitle && (
              <div className={styles.infoRow}>
                <Text className={styles.label}>Job Title:</Text>
                <Text>{selectedUser.jobTitle}</Text>
              </div>
            )}
            {selectedUser.department && (
              <div className={styles.infoRow}>
                <Text className={styles.label}>Department:</Text>
                <Text>{selectedUser.department}</Text>
              </div>
            )}
            {selectedUser.officeLocation && (
              <div className={styles.infoRow}>
                <Text className={styles.label}>Office:</Text>
                <Text>{selectedUser.officeLocation}</Text>
              </div>
            )}
            {selectedUser.manager && (
              <div className={styles.infoRow}>
                <Text className={styles.label}>Line Manager:</Text>
                <Text>{selectedUser.manager.displayName}</Text>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
